import { useEffect } from 'react'

export type LightboxImage = {
  src: string
  alt?: string
  /** Optional filename for the download button */
  downloadName?: string
  /** Optional link to open the source post / profile on bsky.app */
  postUrl?: string
}

export function ImageLightbox({ image, onClose }: { image: LightboxImage; onClose: () => void }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  async function download() {
    try {
      const res = await fetch(image.src, { mode: 'cors' })
      if (!res.ok) throw new Error(`fetch ${res.status}`)
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = image.downloadName ?? deriveName(image.src)
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 5000)
    } catch {
      // Fallback: open in a new tab so the user can save manually
      window.open(image.src, '_blank', 'noopener')
    }
  }

  return (
    <div
      className="fixed inset-0 z-[120] grid place-items-center bg-black/85 p-4 backdrop-blur-md"
      onClick={onClose}
    >
      <div
        className="relative flex max-h-full w-full max-w-5xl flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={image.src}
          alt={image.alt ?? ''}
          className="max-h-[80vh] w-auto rounded-2xl object-contain shadow-glass"
        />
        <div className="glass-tinted flex w-full max-w-3xl flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm">
          <span className="truncate text-ink-200" title={image.src}>
            {image.alt || deriveName(image.src)}
          </span>
          <div className="flex items-center gap-2">
            {image.postUrl ? (
              <a
                className="glass-button text-xs"
                href={image.postUrl}
                target="_blank"
                rel="noreferrer"
              >
                ↗ bsky
              </a>
            ) : null}
            <a
              className="glass-button text-xs"
              href={image.src}
              target="_blank"
              rel="noreferrer"
              title="open source URL"
            >
              source
            </a>
            <button type="button" className="glass-button glass-button--accent text-xs" onClick={download}>
              ⤓ download
            </button>
            <button type="button" className="glass-button text-xs" onClick={onClose}>
              close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function deriveName(url: string): string {
  try {
    const u = new URL(url)
    const last = u.pathname.split('/').filter(Boolean).pop() ?? 'image'
    return last.length > 80 ? `${last.slice(0, 80)}.jpg` : `${last}.jpg`
  } catch {
    return 'image.jpg'
  }
}
